import { codelistRecordKeys } from 'features/codelist/domain/codelistRecordSchema';
import { CODELIST_SIDEBAR_DICTIONARY } from 'features/codelist/sidebar/Dictionary';
import { Button } from 'features/components/Button';
import { Dropdown } from 'features/entry-form-v2/component/Dropdown';
import { useLanguage } from 'features/hooks/useLanguage';
import { useState } from 'react';
import { LanguageType } from 'types';
import {
	ImportedFileFieldNameByCodelistRecordFieldName,
	MappedCodelist
} from '../../ImportCodelistPage';
import { toast } from 'react-toastify';
import { useVariablesQuery } from 'features/entry-form-v2/data/useVariablesQuery/useVariablesQuery';
import { useProjectId } from 'features/hooks/useProjectId';
import { ArrowBigRight } from 'lucide-react';
import { FullReplacementWarning } from './FullReplacementWarning';
import { ImportMode } from '../../ImportCodelistPage';
import { Loader } from 'components/UI/Loader';
import { parseErrors, RowError, RowErrorDetail } from './parseErrors';
import { ValidationErrorTable } from './ValidationErrorTable';

const getAvailableColumns = (
	allColumns: string[],
	mappedColumns: Record<string, string>,
	currentFieldKey: string,
	identifyingField: string | undefined
): string[] => {
	const otherMappedColumns = new Set(
		Object.entries(mappedColumns)
			.filter(([key]) => key !== currentFieldKey)
			.map(([, value]) => value)
	);

	return allColumns
		.filter(col => col !== identifyingField && !otherMappedColumns.has(col))
		.sort((a, b) => a.localeCompare(b));
};

const FieldMappingRow = ({
	codelistRecordKey,
	currentMapping,
	availableColumns,
	onSelect,
	onClear,
	label,
	translation
}: {
	codelistRecordKey: string;
	currentMapping: string | undefined;
	availableColumns: string[];
	onSelect: (column: string) => void;
	onClear: () => void;
	label: string;
	translation: { selectValue: string; clearSelection: string };
}) => (
	<tr>
		<td>
			<Dropdown
				menuDataTestId={`identifying-variable-dropdown_${codelistRecordKey}`}
				placeholder={currentMapping || translation.selectValue}
			>
				{currentMapping && (
					<Dropdown.Item onClick={onClear}>{translation.clearSelection}</Dropdown.Item>
				)}
				{availableColumns.map(column => (
					<Dropdown.Item onClick={() => onSelect(column)} key={column}>
						{column}
					</Dropdown.Item>
				))}
			</Dropdown>
		</td>
		<td>
			<ArrowBigRight className="size-8" />
		</td>
		<td>
			<h3 className="text-base font-bold">{label}</h3>
		</td>
	</tr>
);

export const MapFields = ({
	importedCodelistColumnNames,
	onConfirm,
	onBackClicked,
	rows,
	importMode
}: {
	importedCodelistColumnNames: string[];
	onConfirm: (fieldMapping: MappedCodelist) => void;
	onBackClicked: () => void;
	rows: Record<string, string>[];
	importMode: ImportMode;
}) => {
	const projectId = useProjectId();

	const language = useLanguage();
	const translation = DICTIONARY[language];
	const codelistKeysTranslation = CODELIST_SIDEBAR_DICTIONARY[language];

	const variablesQuery = useVariablesQuery({ projectId });

	const variables = variablesQuery.data?.variables;

	const [
		importedFileFieldNamesByCodelistRecordFieldNames,
		setImportedFileFieldNamesByCodelistRecordFieldNames
	] = useState<ImportedFileFieldNameByCodelistRecordFieldName>(
		{} as ImportedFileFieldNameByCodelistRecordFieldName
	);
	const [codelistIdentifyingFieldName, setCodelistIdentifyingFieldName] = useState<
		string | undefined
	>(undefined);
	const [identifyingVariableName, setIdentifyingVariableName] = useState<string | undefined>(
		undefined
	);
	const [validationErrors, setValidationErrors] = useState<Set<RowError>>();
	const [validationErrorDetails, setValidationErrorDetails] = useState<RowErrorDetail[]>([]);
	const [showCountryCodePrompt, setShowCountryCodePrompt] = useState(false);

	const handleSubmit = (prependCountryCode?: string) => {
		if (!identifyingVariableName) {
			toast.error(translation.variableNameRequired, {
				position: 'top-center'
			});
			return;
		}

		if (!codelistIdentifyingFieldName) {
			toast.error(translation.codelistRowNameRequired, {
				position: 'top-center'
			});
			return;
		}

		const fieldCount = Object.keys(importedFileFieldNamesByCodelistRecordFieldNames).length;

		if (fieldCount < 1) {
			toast.error(translation.atLeastTwoFieldsRequired, {
				position: 'top-center'
			});
			return;
		}

		const parseResult = parseErrors({
			rows,
			language,
			uniqueField: codelistIdentifyingFieldName,
			importedFileFieldNamesByCodelistRecordFieldNames,
			prependCountryCode
		});

		// Show country code prompt ONLY for fixable phone errors
		if (parseResult.hasFixablePhoneErrors && !prependCountryCode && !showCountryCodePrompt) {
			setShowCountryCodePrompt(true);
			return;
		}

		// Show detailed errors if any exist
		if (parseResult.errors.size > 0) {
			setValidationErrors(parseResult.errors);
			setValidationErrorDetails(parseResult.errorDetails);
			setShowCountryCodePrompt(false);
			return;
		}

		// Clear errors on success
		setValidationErrorDetails([]);

		const identifyingVariable = variables?.[identifyingVariableName];
		if (!identifyingVariable) {
			console.error('Identifying variable not found, this is a noop');
		}

		onConfirm({
			entryIdentifyingVariable: {
				name: identifyingVariableName,
				label: identifyingVariable?.variableLabel || 'No variable label'
			},
			codelistIdentifyingRowName: codelistIdentifyingFieldName,
			parsedRecords: parseResult.parsedRecords,
			fieldMapping: importedFileFieldNamesByCodelistRecordFieldNames
		});
	};

	const handleConfirmCountryCode = () => {
		handleSubmit('+47');
	};

	const handleDeclineCountryCode = () => {
		setShowCountryCodePrompt(false);
		// Re-run validation without country code to show the original errors
		if (!codelistIdentifyingFieldName) return;

		const parseResult = parseErrors({
			rows,
			language,
			uniqueField: codelistIdentifyingFieldName,
			importedFileFieldNamesByCodelistRecordFieldNames
		});
		setValidationErrors(parseResult.errors);
		setValidationErrorDetails(parseResult.errorDetails);
	};

	if (variablesQuery.isLoading) {
		return <Loader />;
	}

	return (
		<div className="pb-10">
			<h1 className="text-2xl font-bold">{translation.title}</h1>

			<p className="text-base mt-4">{translation.description}</p>

			{importMode === 'full' && <FullReplacementWarning />}

			<form
				onSubmit={e => {
					e.preventDefault();

					handleSubmit();
				}}
			>
				<div className="flex flex-col gap-2 col-span-full mt-10">
					<h3 className="text-base font-bold">{translation.codelistIdVariable.title}</h3>
					<p className="text-base">{translation.codelistIdVariable.description}</p>

					<div className="mt-4 flex gap-4 items-center">
						<Dropdown
							placeholder={
								codelistIdentifyingFieldName ||
								translation.mapIds.selectCodelistField
							}
						>
							{importedCodelistColumnNames.map(importedCodelistColumnName => (
								<Dropdown.Item
									key={importedCodelistColumnName}
									onClick={() => {
										setCodelistIdentifyingFieldName(importedCodelistColumnName);
									}}
								>
									{importedCodelistColumnName}
								</Dropdown.Item>
							))}
						</Dropdown>

						<ArrowBigRight className="size-8" />

						<Dropdown
							placeholder={
								identifyingVariableName
									? variables?.[identifyingVariableName]?.variableLabel ||
									  translation.mapIds.selectVariable
									: translation.mapIds.selectVariable
							}
						>
							{variables &&
								Object.entries(variables)
									.sort((a, b) =>
										a[1].variableLabel.localeCompare(b[1].variableLabel)
									)
									.map(([, { variableLabel, variableName }]) => (
										<Dropdown.Item
											key={variableName}
											onClick={() => {
												setIdentifyingVariableName(variableName);
											}}
										>
											{variableLabel}
										</Dropdown.Item>
									))}
						</Dropdown>
					</div>
				</div>

				<div className="mt-10">
					<div>
						<h3 className="text-base font-bold">{translation.mapIds.header}</h3>

						<p className="text-base mt-4">{translation.mapIds.description}</p>
					</div>

					<table className="mt-10">
						<thead>
							<tr className="text-base text-start">
								<th>{translation.mapIds.value}</th>
								<th></th>
								<th>{translation.mapIds.codelistRecordField}</th>
							</tr>
						</thead>

						<tbody>
							{codelistRecordKeys
								.filter(key => key !== 'customId')
								.map(codelistRecordKey => (
									<FieldMappingRow
										key={codelistRecordKey}
										codelistRecordKey={codelistRecordKey}
										currentMapping={
											importedFileFieldNamesByCodelistRecordFieldNames[
												codelistRecordKey
											]
										}
										availableColumns={getAvailableColumns(
											importedCodelistColumnNames,
											importedFileFieldNamesByCodelistRecordFieldNames,
											codelistRecordKey,
											codelistIdentifyingFieldName
										)}
										onSelect={column => {
											setImportedFileFieldNamesByCodelistRecordFieldNames(
												prev => ({
													...prev,
													[codelistRecordKey]: column
												})
											);
										}}
										onClear={() => {
											setImportedFileFieldNamesByCodelistRecordFieldNames(
												prev => {
													const { [codelistRecordKey]: _, ...rest } =
														prev;
													return rest as ImportedFileFieldNameByCodelistRecordFieldName;
												}
											);
										}}
										label={codelistKeysTranslation[codelistRecordKey].label}
										translation={translation}
									/>
								))}
						</tbody>
					</table>
				</div>

				{validationErrorDetails.length > 0 && !showCountryCodePrompt && (
					<ValidationErrorTable
						errorDetails={validationErrorDetails}
						language={language}
					/>
				)}

				{showCountryCodePrompt && (
					<div className="bg-warning-500/10 border border-warning-500 rounded-md px-6 py-4 mt-4">
						<h3 className="text-lg text-warning-700 font-bold">
							{translation.countryCodePrompt.title}
						</h3>

						<p className="text-base mt-2">
							{translation.countryCodePrompt.description}
						</p>

						<div className="mt-4 flex gap-4">
							<Button
								type="button"
								onClick={handleConfirmCountryCode}
								text={translation.countryCodePrompt.confirm}
							/>
							<Button
								variant="secondary"
								type="button"
								onClick={handleDeclineCountryCode}
								text={translation.countryCodePrompt.decline}
							/>
						</div>
					</div>
				)}

				<div className="mt-4 flex gap-4">
					<Button
						variant="secondary"
						type="button"
						onClick={onBackClicked}
						text={translation.back}
					/>
					<Button type="submit" text={translation.confirm} />
				</div>
			</form>
		</div>
	);
};

const DICTIONARY = {
	[LanguageType.English]: {
		title: 'Map fields',
		description:
			'Choose the fields in the imported file that represents the fields in the codelist. The only required field is the codelist ID, you may .',

		codelistIdVariable: {
			title: 'Variable for codelist id',
			description:
				'Here you select the variable in the dataset that will be used to identify the row in the codelist.'
		},

		mapIds: {
			header: 'Remaining fields',
			description:
				'Here you select which fields in the imported file that represents the fields in the codelist. Empty fields are OK, but you must include at least one field.',

			codelistRecordField: 'Fields in Ledidi',
			value: 'Column in imported file',
			selectVariable: 'Select variable',
			selectCodelistField: 'Select codelist field'
		},

		variableNameRequired: 'Variable name is required',
		codelistRowNameRequired: 'Codelist row name is required',

		atLeastTwoFieldsRequired: 'At least two fields are required',

		selectValue: 'Select value',
		clearSelection: 'Clear selection',

		confirm: 'Confirm',
		back: 'Back',

		countryCodePrompt: {
			title: 'Missing country code',
			description:
				'Some phone numbers are missing a country code. We require all phone numbers to have a country code. Would you like to automatically prepend +47 to phone numbers that are missing a country code? For more advanced validation, you must update phone numbers with country code before importing.',
			confirm: 'Yes, prepend +47',
			decline: 'No, show errors'
		}
	},

	[LanguageType.Norwegian]: {
		title: 'Sammenstill felter',
		description:
			'Velg hvilke felter i den importerte filen som hører til feltene i kodelisten i Ledidi. Det eneste påkrevde feltet er kodeliste ID, resten kan du la være å inkludere.',

		codelistIdVariable: {
			title: 'Variabel for kodeliste ID',
			description:
				'Her velger du hvilken variabel i datasettet som skal brukes til å identifisere raden i kodelisten.'
		},

		mapIds: {
			header: 'Resterende felter',
			description:
				'Her velger du hvilke resterende felter du vil inkludere. Tomme felter er OK, men du må inkludere minst ett felt.',

			codelistRecordField: 'Felt i Ledidi',
			value: 'Kolonne i importert fil',
			selectVariable: 'Velg variabel',
			selectCodelistField: 'Velg felt i kodeliste'
		},

		variableNameRequired: 'Variabelnavn er påkrevd',
		codelistRowNameRequired: 'Rad i kodeliste er påkrevd',

		atLeastTwoFieldsRequired: 'Minst to felter er påkrevd',

		selectValue: 'Velg verdi',
		clearSelection: 'Fjern valg',

		confirm: 'Bekreft',
		back: 'Tilbake',

		countryCodePrompt: {
			title: 'Manglende landskode',
			description:
				'Noen telefonnummer mangler landskode, og vi krever at alle telefonnummer har en landskode. Vil du automatisk legge til +47 foran telefonnummer som mangler landskode? For mer avansert kontroll må du oppdatere nummer med landskode før du importerer.',
			confirm: 'Ja, legg til +47',
			decline: 'Nei, vis feil'
		}
	}
} as const;
